import React, { useCallback, useState } from "react";
import FormattedText from "../../../FormattedText/FormattedText";
import LandingButton from "../../../LandingButton/LandingButton";
import { useTravellerDetailsContext } from "../../contexts/TravellerDetailsV2Context";
import {
  Amount,
  Bar,
  BottomSheet,
  InfoBtn,
  Overlay,
  PriceCol,
  PriceRow,
  PriceRowLabel,
  PriceRowValue,
  SheetCloseBtn,
  SheetContent,
  SheetTitle,
  SheetTopBar,
  Sub,
  TotalLabel,
  TotalLabelMain,
  TotalLabelSub,
  TotalRow,
  TotalValue,
} from "./Styles";

import { InfoIcon, CloseIcon } from "../../../CommonLandingMweb/shared/MwebIcons";

type PricingDetail = {
  label: string;
  amount: {
    text: {
      value: string;
      styles?: { color?: string; fontWeight?: string };
    };
  };
  description?: string;
};

type PriceBreakupData = {
  title?: string;
  pricingDetails?: PricingDetail[];
};

function pickLabel(node: unknown): unknown {
  if (node && typeof node === "object" && "title" in (node as object)) {
    return (node as { title?: unknown }).title;
  }
  return node;
}

function pickSub(node: unknown): unknown {
  if (node && typeof node === "object" && "subtitle" in (node as object)) {
    return (node as { subtitle?: unknown }).subtitle;
  }
  return null;
}

function pickBreakup(node: unknown): PriceBreakupData | null {
  if (
    node &&
    typeof node === "object" &&
    "priceBreakup" in (node as object)
  ) {
    return (node as { priceBreakup?: PriceBreakupData }).priceBreakup ?? null;
  }
  return null;
}

export const BuyNowBar = ({
  buyButtonTxt,
  amountInfo,
  subtitle,
}: {
  buyButtonTxt?: string;
  amountInfo?: unknown;
  subtitle?: unknown;
}) => {
  const { onBuy } = useTravellerDetailsContext();
  const [showBreakup, setShowBreakup] = useState(false);

  const amountNode = pickLabel(amountInfo);
  const subNode = pickSub(amountInfo) || pickLabel(subtitle);
  const breakup = pickBreakup(amountInfo);

  const openBreakup = useCallback(() => {
    if (breakup?.pricingDetails?.length) setShowBreakup(true);
  }, [breakup]);

  const closeBreakup = useCallback(() => setShowBreakup(false), []);

  const lastIdx = (breakup?.pricingDetails?.length ?? 0) - 1;

  return (
    <>
      <Bar role="contentinfo">
        <PriceCol>
          {amountNode ? (
            <Amount>
              <FormattedText value={amountNode as any} />
              {breakup?.pricingDetails?.length ? (
                <InfoBtn
                  type="button"
                  onClick={openBreakup}
                  aria-label="View price breakup"
                >
                  <InfoIcon />
                </InfoBtn>
              ) : null}
            </Amount>
          ) : null}
          {subNode ? (
            <Sub>
              <FormattedText value={subNode as any} />
            </Sub>
          ) : null}
        </PriceCol>
        {buyButtonTxt ? (<LandingButton
          customTitle={buyButtonTxt}
          onClick={onBuy}
          customWidth="148px"
          flexShrink="0"
        />) : null}
      </Bar>

      {showBreakup && breakup?.pricingDetails?.length ? (
        <>
          <Overlay onClick={closeBreakup} />
          <BottomSheet>
            <SheetTopBar>
              <SheetCloseBtn
                type="button"
                onClick={closeBreakup}
                aria-label="Close"
              >
                <CloseIcon />
              </SheetCloseBtn>
              <SheetTitle><FormattedText value={breakup.title} /></SheetTitle>
            </SheetTopBar>
            <SheetContent>
              {breakup.pricingDetails.map((detail, idx) => {
                const isLast = idx === lastIdx;
                if (isLast) {
                  return (
                    <TotalRow key={`pb-${idx}`}>
                      <TotalLabel>
                        <TotalLabelMain>{detail.label}</TotalLabelMain>
                        {detail.description ? (
                          <TotalLabelSub>{detail.description}</TotalLabelSub>
                        ) : null}
                      </TotalLabel>
                      <TotalValue>
                        <FormattedText value={detail.amount} />
                      </TotalValue>
                    </TotalRow>
                  );
                }
                return (
                  <PriceRow key={`pb-${idx}`}>
                    <PriceRowLabel>{detail.label}</PriceRowLabel>
                    <PriceRowValue>
                      <FormattedText value={detail.amount} />
                    </PriceRowValue>
                  </PriceRow>
                );
              })}
            </SheetContent>
          </BottomSheet>
        </>
      ) : null}
    </>
  );
};
